// Guestbook panel — the HTML overlay behind the viewer's "📖 Guestbook" pill.
//
// Two modes off one component:
//   • visitor — leave a note for the creator, then read their replies below
//   • owner   — the inbox for this world: every note, unread ones flagged, with
//               a reply box under each thread
//
// Inputs here are plain <textarea>/<input> elements, so the app's existing
// "ignore hotkeys while typing" guards (tagName === 'INPUT' | 'TEXTAREA', e.g.
// EmoteWheel's isTypingTarget and WorldBuilder's keydown guard) already apply.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  addEntry,
  fetchEntriesForWorld,
  fetchMyEntries,
  markRead,
  type GuestbookThread,
} from '../services/guestbookService';

interface GuestbookPanelProps {
  worldId: string;
  isOwner: boolean;
  onClose: () => void;
  /** Fired after loads / mark-read so the HUD badge can follow along. */
  onCountsChange?: (counts: { total: number; unread: number }) => void;
}

const TEAL = '#00c2b3';
const FONT = 'Outfit, sans-serif';

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '';
  const secs = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (secs < 60) return 'just now';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

const panelStyle: React.CSSProperties = {
  position: 'absolute', left: 20, bottom: 72, zIndex: 60,
  width: 380, maxWidth: 'calc(100vw - 40px)', maxHeight: '62vh',
  display: 'flex', flexDirection: 'column',
  background: 'rgba(10,22,38,0.92)', backdropFilter: 'blur(14px)',
  border: '1px solid rgba(0,194,179,0.25)', borderRadius: 18,
  boxShadow: '0 24px 80px rgba(0,0,0,0.5)',
  color: '#eaf6f8', fontFamily: FONT,
};

const fieldStyle: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', resize: 'none',
  background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.14)',
  borderRadius: 12, padding: '10px 12px', color: '#eaf6f8',
  fontFamily: FONT, fontSize: 13.5, lineHeight: 1.5, outline: 'none',
};

const primaryBtn: React.CSSProperties = {
  padding: '9px 18px', borderRadius: 999, border: 'none', cursor: 'pointer',
  background: `linear-gradient(135deg, ${TEAL}, #0077b6)`, color: '#fff',
  fontFamily: FONT, fontSize: 13.5, fontWeight: 700,
};

const GuestbookPanel: React.FC<GuestbookPanelProps> = ({ worldId, isOwner, onClose, onCountsChange }) => {
  const [threads, setThreads] = useState<GuestbookThread[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [replyDraft, setReplyDraft] = useState('');
  const panelRef = useRef<HTMLDivElement>(null);

  // Keep pointer events inside the panel — otherwise the canvas controls behind
  // it grab the drag (same guard ChatOverlay uses).
  useEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    const stop = (e: Event) => e.stopPropagation();
    const events = ['pointerdown', 'pointermove', 'pointerup', 'mousedown', 'mousemove', 'mouseup', 'wheel', 'touchstart', 'touchmove', 'touchend'];
    events.forEach((evt) => el.addEventListener(evt, stop, { capture: true }));
    return () => { events.forEach((evt) => el.removeEventListener(evt, stop, { capture: true })); };
  }, []);

  // Held in a ref so a fresh inline callback from the parent can't retrigger the
  // load effect (reporting counts re-renders the parent, which would loop).
  const onCountsChangeRef = useRef(onCountsChange);
  onCountsChangeRef.current = onCountsChange;

  const report = useCallback((list: GuestbookThread[]) => {
    onCountsChangeRef.current?.({
      total: list.length,
      unread: list.filter((t) => !t.entry.read_by_owner).length,
    });
  }, []);

  const load = useCallback(async () => {
    setError(null);
    try {
      const list = isOwner ? await fetchEntriesForWorld(worldId) : await fetchMyEntries(worldId);
      setThreads(list);
      report(list);
      return list;
    } catch (err) {
      console.error('[Guestbook] load failed:', err);
      setError('Could not load the guestbook.');
      return [];
    } finally {
      setLoading(false);
    }
  }, [worldId, isOwner, report]);

  // Opening the owner inbox counts as reading it.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const list = await load();
      if (cancelled || !isOwner) return;
      const unread = list.filter((t) => !t.entry.read_by_owner);
      if (!unread.length) return;
      try {
        await Promise.all(unread.map((t) => markRead(t.entry.id)));
      } catch (err) {
        console.error('[Guestbook] mark read failed:', err);
        return;
      }
      if (cancelled) return;
      const seen = new Set(unread.map((t) => t.entry.id));
      setThreads((prev) => {
        const next = prev.map((t) => (seen.has(t.entry.id) ? { ...t, entry: { ...t.entry, read_by_owner: true } } : t));
        report(next);
        return next;
      });
    })();
    return () => { cancelled = true; };
  }, [load, isOwner, report]);

  const submitNote = async () => {
    if (!draft.trim() || sending) return;
    setSending(true);
    setError(null);
    try {
      await addEntry(worldId, draft);
      setDraft('');
      setSent(true);
      setTimeout(() => setSent(false), 3000);
      await load();
    } catch (err: any) {
      console.error('[Guestbook] note failed:', err);
      setError(err?.message || 'Could not leave your note.');
    } finally {
      setSending(false);
    }
  };

  const submitReply = async (parentId: string) => {
    if (!replyDraft.trim() || sending) return;
    setSending(true);
    setError(null);
    try {
      await addEntry(worldId, replyDraft, parentId);
      setReplyDraft('');
      setReplyTo(null);
      await load();
    } catch (err: any) {
      console.error('[Guestbook] reply failed:', err);
      setError(err?.message || 'Could not send your reply.');
    } finally {
      setSending(false);
    }
  };

  const renderThread = (t: GuestbookThread) => {
    const unread = isOwner && !t.entry.read_by_owner;
    return (
      <div
        key={t.entry.id}
        style={{
          background: 'rgba(255,255,255,0.04)',
          borderLeft: `3px solid ${unread ? TEAL : 'rgba(255,255,255,0.12)'}`,
          borderRadius: 10, padding: '10px 12px', marginBottom: 10,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: unread ? TEAL : '#eaf6f8' }}>
            {t.entry.author_name}
          </span>
          <span style={{ fontSize: 11.5, color: 'rgba(234,246,248,0.45)' }}>{relativeTime(t.entry.created_at)}</span>
        </div>
        <div style={{ fontSize: 13.5, lineHeight: 1.55, color: 'rgba(234,246,248,0.85)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {t.entry.message}
        </div>

        {t.replies.map((r) => (
          <div key={r.id} style={{ marginTop: 8, marginLeft: 10, paddingLeft: 10, borderLeft: '2px solid rgba(0,194,179,0.35)' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: TEAL, marginBottom: 2 }}>
              {r.author_name} <span style={{ fontWeight: 400, color: 'rgba(234,246,248,0.4)' }}>· {relativeTime(r.created_at)}</span>
            </div>
            <div style={{ fontSize: 13, lineHeight: 1.5, color: 'rgba(234,246,248,0.8)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {r.message}
            </div>
          </div>
        ))}

        {isOwner && (
          replyTo === t.entry.id ? (
            <div style={{ marginTop: 10, display: 'flex', gap: 6 }}>
              <input
                autoFocus
                value={replyDraft}
                maxLength={1000}
                placeholder="Write a reply…"
                onChange={(e) => setReplyDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') submitReply(t.entry.id); }}
                style={{ ...fieldStyle, flex: 1, padding: '8px 10px', fontSize: 13 }}
              />
              <button
                onClick={() => submitReply(t.entry.id)}
                disabled={sending || !replyDraft.trim()}
                style={{ ...primaryBtn, padding: '8px 14px', fontSize: 12.5, opacity: sending || !replyDraft.trim() ? 0.5 : 1 }}
              >
                Send
              </button>
            </div>
          ) : (
            <button
              onClick={() => { setReplyTo(t.entry.id); setReplyDraft(''); }}
              style={{
                marginTop: 8, background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                color: TEAL, fontFamily: FONT, fontSize: 12.5, fontWeight: 600,
              }}
            >
              Reply
            </button>
          )
        )}
      </div>
    );
  };

  return (
    <div ref={panelRef} className="gx-guestbook" style={panelStyle}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '14px 16px 10px', borderBottom: '1px solid rgba(255,255,255,0.08)',
      }}>
        <div>
          <div style={{ fontSize: 15.5, fontWeight: 700 }}>📖 Guestbook</div>
          <div style={{ fontSize: 11.5, color: 'rgba(234,246,248,0.5)', marginTop: 2 }}>
            {isOwner ? 'Notes visitors left in this world' : 'Leave the creator a note'}
          </div>
        </div>
        <button
          onClick={onClose}
          aria-label="Close guestbook"
          style={{
            background: 'none', border: 'none', color: 'rgba(234,246,248,0.6)',
            fontSize: 18, cursor: 'pointer', lineHeight: 1, padding: 4, fontFamily: FONT,
          }}
        >
          ✕
        </button>
      </div>

      <div style={{ padding: '12px 16px 16px', overflowY: 'auto' }}>
        {!isOwner && (
          <div style={{ marginBottom: 16 }}>
            <textarea
              rows={3}
              value={draft}
              maxLength={1000}
              placeholder="Say hello, leave feedback, ask a question…"
              onChange={(e) => setDraft(e.target.value)}
              style={fieldStyle}
            />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
              <span style={{ fontSize: 11.5, color: 'rgba(234,246,248,0.4)' }}>
                {sent ? 'Sent — the creator has been notified ✓' : `${draft.length}/1000`}
              </span>
              <button
                onClick={submitNote}
                disabled={sending || !draft.trim()}
                style={{ ...primaryBtn, opacity: sending || !draft.trim() ? 0.5 : 1 }}
              >
                {sending ? 'Sending…' : 'Leave note'}
              </button>
            </div>
          </div>
        )}

        {error && (
          <div style={{ fontSize: 12.5, color: '#ff8080', marginBottom: 10 }}>{error}</div>
        )}

        {!isOwner && (
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'rgba(234,246,248,0.45)', marginBottom: 8 }}>
            Your notes
          </div>
        )}

        {loading ? (
          <div style={{ fontSize: 13, color: 'rgba(234,246,248,0.5)' }}>Loading…</div>
        ) : threads.length === 0 ? (
          <div style={{ fontSize: 13, color: 'rgba(234,246,248,0.5)', lineHeight: 1.6 }}>
            {isOwner ? 'No notes yet — visitors can sign your guestbook from here.' : 'You haven’t left a note here yet.'}
          </div>
        ) : (
          threads.map(renderThread)
        )}
      </div>
    </div>
  );
};

export default GuestbookPanel;
